import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';

const BRANDS = [
  "RJ",
  "Winclash",
  "Winmatch",
  "Winexch",
  "Betinexch",
  "Playin",
  "Cricaza",
  "Spinmatch",
  "Khelo",
  "Winstarexch",
  "Starexch"
];

const LOCATION_SCHEDULE = [
  { day: 'Sunday', location: 'Mumbai,Maharashtra,India' },
  { day: 'Monday', location: 'Delhi,Delhi,India' },
  { day: 'Tuesday', location: 'Bengaluru,Karnataka,India' },
  { day: 'Wednesday', location: 'Chennai,Tamil Nadu,India' },
  { day: 'Thursday', location: 'Kolkata,West Bengal,India' },
  { day: 'Friday', location: 'Hyderabad,Telangana,India' },
  { day: 'Saturday', location: 'Pune,Maharashtra,India' }
];

const SeoDashboard = () => {
  const currentDayIndex = new Date().getDay();
  const todaysLocation = LOCATION_SCHEDULE[currentDayIndex].location;
  const [metrics, setMetrics] = useState({
    totalChecked: 0,
    totalPassed: 0,
    totalFailed: 0,
    passRatePercentage: 0
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters State
  const [filters, setFilters] = useState({
    brand: 'ALL',
    location: todaysLocation, // Default to today's scheduled location
    status: 'ALL',
    startDate: '',
    endDate: ''
  });

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const metricsRes = await fetch(`${process.env.REACT_APP_API_URL}/seo-results/metrics`);
      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }

      const resultsRes = await fetch(`${process.env.REACT_APP_API_URL}/seo-results`);
      if (resultsRes.ok) {
        const resultsData = await resultsRes.json();
        setResults(resultsData);
      }
    } catch (error) {
      console.error('Error fetching SEO dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Filter Logic
  const filteredResults = useMemo(() => {
    return results.filter(result => {
      // Brand filter
      if (filters.brand && filters.brand !== 'ALL' && result.brand?.toLowerCase() !== filters.brand.toLowerCase()) {
        return false;
      }
      
      // Location filter
      if (filters.location && filters.location !== 'ALL' && result.location?.toLowerCase() !== filters.location.toLowerCase()) {
        return false;
      }
      
      // Status filter
      if (filters.status !== 'ALL' && result.status?.toUpperCase() !== filters.status) {
        return false;
      }
      
      // Date filter
      if (filters.startDate) {
        if (new Date(result.timestamp) < new Date(filters.startDate)) return false;
      }
      if (filters.endDate) {
        // Add 1 day to end date to make it inclusive for that day
        const end = new Date(filters.endDate);
        end.setDate(end.getDate() + 1);
        if (new Date(result.timestamp) >= end) return false;
      }

      return true;
    });
  }, [results, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      brand: 'ALL',
      location: 'ALL', // Or we could reset back to todaysLocation, but 'ALL' is safer for a "Clear" button
      status: 'ALL',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  const handleDatePreset = (preset) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (preset === 'today') {
      // both start and end are today
    } else if (preset === 'this-week') {
      const day = today.getDay(); // 0 is Sunday, 1 is Monday
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
      start = new Date(today.setDate(diff));
      end = new Date();
    } else if (preset === 'this-month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date();
    }

    setFilters(prev => ({
      ...prev,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    }));
    setCurrentPage(1);
  };

  // Sorting Logic
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedResults = useMemo(() => {
    const sortableItems = [...filteredResults];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle specific types
        if (sortConfig.key === 'timestamp') {
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
        } else if (sortConfig.key === 'avgRank') {
          // Put nulls/undefined at the end
          aValue = aValue || 999999;
          bValue = bValue || 999999;
        } else {
          aValue = aValue ? aValue.toString().toLowerCase() : '';
          bValue = bValue ? bValue.toString().toLowerCase() : '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredResults, sortConfig]);

  // Pagination Logic
  const totalPages = Math.ceil(sortedResults.length / rowsPerPage);
  
  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * rowsPerPage;
    const lastPageIndex = firstPageIndex + rowsPerPage;
    return sortedResults.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, rowsPerPage, sortedResults]);

  useEffect(() => {
    // If we filter data down and the current page is now empty, bump back
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const exportToExcel = () => {
    if (filteredResults.length === 0) return;
    
    // Select complete details for a comprehensive modern export
    const exportData = filteredResults.map(r => {
      let compStr = 'N/A';
      if (r.topCompetitors) {
        try {
          const comps = typeof r.topCompetitors === 'string' ? JSON.parse(r.topCompetitors) : r.topCompetitors;
          if (Array.isArray(comps)) compStr = comps.join(', ');
        } catch(e) {}
      }

      return {
        'Execution Date & Time': new Date(r.timestamp).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }),
        'Execution ID': r.executionId,
        'Brand': r.brand || 'N/A',
        'Keyword': r.keyword || 'N/A',
        'Target Domain': r.targetDomain || 'N/A',
        'Location': r.location || 'N/A',
        'Average Rank': r.avgRank || 'N/A',
        'Status': r.status?.toUpperCase(),
        'Top Competitors': compStr,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SEO Results");
    
    // Generate buffer and save file
    XLSX.writeFile(workbook, `SEO_Results_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
      <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-700 to-indigo-500">
            SEO Results Reporting
          </h1>
          <p className="text-gray-500 mt-2 text-sm font-medium">Overview of your latest search engine placements</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
          <button 
            onClick={exportToExcel}
            disabled={filteredResults.length === 0}
            className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-700 font-semibold rounded-lg hover:bg-green-100 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed border border-green-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to Excel
          </button>
          
          <button 
            onClick={fetchDashboardData}
            className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-5 py-2.5 bg-indigo-50 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-100 transition-colors shadow-sm border border-indigo-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard 
          title="Total Checked" 
          value={metrics.totalChecked} 
          icon={
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
          }
        />
        <MetricCard 
          title="Passed Placements" 
          value={metrics.totalPassed} 
          valueClass="text-green-600"
          icon={
            <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
        />
        <MetricCard 
          title="Failed Placements" 
          value={metrics.totalFailed} 
          valueClass="text-red-600"
          icon={
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
        />
        <MetricCard 
          title="Pass Rate" 
          value={`${metrics.passRatePercentage}%`} 
          valueClass={metrics.passRatePercentage > 80 ? 'text-green-600' : (metrics.passRatePercentage > 50 ? 'text-yellow-600' : 'text-red-600')}
          icon={
            <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          }
        />
      </div>

      {/* Filters Section */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Brand</label>
            <select 
              name="brand" 
              value={filters.brand || 'ALL'} 
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="ALL">All Brands</option>
              {BRANDS.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Location Schedule
            </label>
            <select 
              name="location" 
              value={filters.location || 'ALL'} 
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white truncate"
            >
              <option value="ALL">All Locations</option>
              {LOCATION_SCHEDULE.map(item => (
                <option key={item.day} value={item.location}>
                  {item.day} - {item.location} {item.location === todaysLocation ? '(Today)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
            <select 
              name="status" 
              value={filters.status} 
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="PASS">Pass</option>
              <option value="FAIL">Fail</option>
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Start Date</label>
            <input 
              type="date" 
              name="startDate" 
              value={filters.startDate} 
              onChange={handleFilterChange} 
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700"
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">End Date</label>
            <input 
              type="date" 
              name="endDate" 
              value={filters.endDate} 
              onChange={handleFilterChange} 
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-700"
            />
          </div>
          <div>
            <button 
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 text-sm font-medium rounded-md transition-colors h-[38px] flex items-center"
            >
              Clear
            </button>
          </div>
        </div>
        
        {/* Quick Report Presets */}
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
           <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2 self-center">Quick Reports:</span>
           <button 
             onClick={() => handleDatePreset('today')}
             className="px-3 py-1.5 text-xs font-medium rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
           >
             Today
           </button>
           <button 
             onClick={() => handleDatePreset('this-week')}
             className="px-3 py-1.5 text-xs font-medium rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
           >
             This Week
           </button>
           <button 
             onClick={() => handleDatePreset('this-month')}
             className="px-3 py-1.5 text-xs font-medium rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
           >
             This Month
           </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">Recent Executions</h2>
          <span className="text-sm text-gray-500 font-medium bg-white px-3 py-1 rounded-full border border-gray-200">
            Showing {filteredResults.length} result(s)
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                <SortableHeader label="Date / Time" sortKey="timestamp" sortConfig={sortConfig} handleSort={handleSort} />
                <SortableHeader label="Brand" sortKey="brand" sortConfig={sortConfig} handleSort={handleSort} />
                <SortableHeader label="Keyword" sortKey="keyword" sortConfig={sortConfig} handleSort={handleSort} />
                <SortableHeader label="Target Domain" sortKey="targetDomain" sortConfig={sortConfig} handleSort={handleSort} />
                <SortableHeader label="Location" sortKey="location" sortConfig={sortConfig} handleSort={handleSort} />
                <SortableHeader label="Avg Rank" sortKey="avgRank" sortConfig={sortConfig} handleSort={handleSort} className="text-center" />
                <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} handleSort={handleSort} className="text-center" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                // Skeleton Loading State
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-36"></div></td>
                    <td className="px-6 py-4 flex justify-center"><div className="h-4 bg-gray-200 rounded w-8"></div></td>
                    <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-16 mx-auto"></div></td>
                  </tr>
                ))
              ) : currentTableData.length > 0 ? (
                currentTableData.map((result, idx) => (
                  <tr key={result.executionId || idx} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                      {new Date(result.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                      {result.brand}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                      {result.keyword}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <span className="bg-gray-100 text-gray-600 py-1 px-2 rounded font-mono text-xs">
                        {result.targetDomain}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {result.location || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800 font-medium text-center">
                      {result.avgRank || '-'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {result.status?.toUpperCase() === 'PASS' ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold leading-4 bg-green-100 text-green-700 shadow-sm border border-green-200">
                          PASS
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold leading-4 bg-red-100 text-red-700 shadow-sm border border-red-200">
                          FAIL
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="text-center py-16 text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium text-gray-900">No results found</p>
                      <p className="mt-1 text-sm text-gray-500">Adjust filters or execute a new test.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {!loading && filteredResults.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 shrink-0">Rows per page:</span>
              <select 
                value={rowsPerPage} 
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
              >
                {[10, 25, 50, 100].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, icon, valueClass = "text-gray-800" }) => (
  <div className="bg-white rounded-xl p-6 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-gray-100 hover:shadow-md transition-shadow relative overflow-hidden group">
    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity transform group-hover:scale-110 duration-300">
      {icon}
    </div>
    <div className="flex items-center gap-4 mb-4">
      <div className="p-3 rounded-lg bg-gray-50/80 ring-1 ring-gray-100 shadow-sm">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-widest">{title}</h3>
    </div>
    <div className={`text-3xl font-bold tracking-tight ${valueClass}`}>
      {value}
    </div>
  </div>
);

const SortableHeader = ({ label, sortKey, sortConfig, handleSort, className = "" }) => {
  const isActive = sortConfig.key === sortKey;
  
  return (
    <th 
      className={`px-6 py-4 font-semibold border-b cursor-pointer hover:bg-gray-100 transition-colors group ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
        {label}
        <span className="opacity-0 group-hover:opacity-50 transition-opacity flex flex-col text-[10px] leading-[0.5] mt-0.5">
          <svg className={`w-3 h-3 ${isActive && sortConfig.direction === 'asc' ? 'text-indigo-600 opacity-100' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" /></svg>
          <svg className={`w-3 h-3 -mt-1 ${isActive && sortConfig.direction === 'desc' ? 'text-indigo-600 opacity-100' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
        </span>
      </div>
    </th>
  );
};

export default SeoDashboard;
